import { createInertiaApp, router } from '@inertiajs/vue3'

import '@fortawesome/fontawesome-pro/css/fontawesome.css'
import '@fortawesome/fontawesome-pro/css/sharp-regular.css'
import '@fortawesome/fontawesome-pro/css/sharp-solid.css'
import '@fortawesome/fontawesome-pro/css/thin.css'
import '@fortawesome/fontawesome-pro/css/brands.css'
import '../css/app.css'
import * as Sentry from '@sentry/vue'
import addons from './addons'
import { SITE_EVENTS, dispatchSiteEvent } from './events'
import filters from './filters'
import helpers from './helpers'
import integrations from './integrations'
import modules from './modules'

createInertiaApp({
  progress: {
    color: 'var(--color-primary-500, #309eaa)',
    // Inertia defaults to 250ms. In practice the only visit that reliably
    // crosses that is a property search — quick, but not quite quick enough —
    // so the bar flashed on what already felt instant. The timer is cancelled
    // if the visit finishes first, so raising it doesn't delay the bar on a
    // genuinely slow load; it just stops the near-instant ones showing it.
    delay: 750,
  },
  withApp(app, { ssr }) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_ENV,
      // Sentry 11 replaced `sendDefaultPii` with per-category settings, which
      // default to collecting everything. User info (the visitor's IP) is the
      // part we relied on, so it is stated rather than left to the default.
      dataCollection: { userInfo: true },
      // Sentry 11 attaches a stack trace to every event, which regroups
      // existing issues; keep them attached to errors only, as before.
      attachStacktrace: false,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0.1,
      ignoreErrors: ['node is null', 'instance is null', 'Request aborted', 'Request failed', 'Network Error', 'Precognition response', /^canceled$/],
      integrations: [
        // No `router` option: that's for Vue Router. Inertia navigates via
        // history.pushState, which the default browser tracing already
        // instruments as navigation transactions.
        Sentry.browserTracingIntegration(),
        // Sentry 11 starts one session per page load; keep one per Inertia
        // navigation so release health compares with what it reported before.
        Sentry.browserSessionIntegration({ lifecycle: 'route' }),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
          maskAllInputs: false,
        }),
      ],
    })

    app.use(modules).use(filters).use(helpers).use(integrations).use(addons)

    if (!ssr) {
      router.on('before', (event) => {
        if (event.detail.visit.prefetch) {
          return
        }
        document.documentElement.classList.remove('scroll-smooth')
      })
      // Inertia fires `navigate` once on the initial page load too, so skip
      // that first occurrence: the initial page view is already sent by the
      // integration's own config (e.g. GA's `gtag('config', …)`). Every
      // subsequent navigation is a genuine SPA page view.
      let initialNavigate = true
      router.on('navigate', () => {
        document.documentElement.classList.add('scroll-smooth')
        if (initialNavigate) {
          initialNavigate = false
          return
        }
        dispatchSiteEvent(SITE_EVENTS.routeChanged, { url: window.location.href })
      })
    }
  },
})
